import React from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import {
  updateVehicleAsync,
  createVehicleAsync,
} from "../../../slices/vehicle";
import { PMField } from "../../utils/PMField";
import { mapErrors } from "../../Utils";

export const VehicleForm = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { vehicle, onClose } = props;

  const createOrUpdateDispatch = (data) => {
    if (vehicle?.id) {
      const res = dispatch(updateVehicleAsync(data));
      return res;
    } else {
      const res = dispatch(createVehicleAsync(data));
      return res;
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          id: vehicle?.id,
          number_plate: vehicle?.number_plate || "",
          nickname: vehicle?.nickname || "",
          description: vehicle?.description || "",
          make: vehicle?.make || "",
          model: vehicle?.model || "",
          model_year: vehicle?.model_year || "",
          body_style: vehicle?.body_style || "",
          tags: vehicle?.tags || "",
          active: vehicle?.active !== undefined ? vehicle.active : true,
          norwegian: vehicle?.norwegian !== undefined ? vehicle.norwegian : true,
          submit: null,
        }}
        validationSchema={Yup.object({
          number_plate: Yup.string()
            .max(20)
            .required(t("Number plate is required")),
          nickname: Yup.string().max(100),
          description: Yup.string().max(500),
          make: Yup.string().max(50),
          model: Yup.string().max(50),
          model_year: Yup.number()
            .min(1900)
            .max(new Date().getFullYear() + 1),
          body_style: Yup.string().max(50),
          tags: Yup.string(),
          norwegian: Yup.boolean(),
        })}
        onSubmit={(values, helpers) => {
          try {
            toast.promise(createOrUpdateDispatch(values), {
              loading: t("Saving...."),
              success: (data) => {
                if (data.status === 500) throw new Error("Server error");
                if (data.payload.errors)
                  throw new Error(mapErrors(data.payload.errors));
                onClose(true);
                return t("Vehicle was saved!");
              },
              error: (error) => {
                return t("Something went wrong. Could not save vehicle!");
              },
            });
            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
          } catch (err) {
            toast.error("Something went wrong...");
            console.error(err);
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err.message });
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form>
            <div className="grid grid-cols-6 gap-6 mt-10">
              {/* Number Plate - Required */}
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="number_plate"
                  type="text"
                  label="Number Plate"
                  placeholder="Enter vehicle number plate"
                  {...formik}
                />
              </div>

              {/* Nickname */}
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="nickname"
                  type="text"
                  label="Nickname"
                  placeholder="Enter vehicle nickname"
                  {...formik}
                />
              </div>

              {/* Make */}
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="make"
                  type="text"
                  label="Make"
                  placeholder="Enter vehicle make"
                  {...formik}
                />
              </div>

              {/* Model */}
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="model"
                  type="text"
                  label="Model"
                  placeholder="Enter vehicle model"
                  {...formik}
                />
              </div>

              {/* Model Year */}
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="model_year"
                  type="number"
                  label="Model Year"
                  placeholder="2024"
                  {...formik}
                />
              </div>

              {/* Body Style */}
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="body_style"
                  type="text"
                  label="Body Style"
                  placeholder="Sedan, SUV, Truck, etc."
                  {...formik}
                />
              </div>

              {/* Active Status */}
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="active"
                  type="checkbox"
                  label="Active Vehicle"
                  description="Check if this vehicle is currently active"
                  {...formik}
                />
              </div>

              {/* Norwegian Vehicle */}
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="norwegian"
                  type="checkbox"
                  label="Norwegian Vehicle"
                  description="Check if this is a Norwegian vehicle (required for SVV integration)"
                  {...formik}
                />
              </div>

              {/* Description */}
              <div className="col-span-6">
                <PMField
                  name="description"
                  type="text"
                  label="Description"
                  multiline
                  rows={3}
                  placeholder="Enter vehicle description"
                  {...formik}
                />
              </div>

              {/* Tags */}
              <div className="col-span-6">
                <PMField
                  name="tags"
                  type="text"
                  label="Tags"
                  placeholder="Enter tags separated by commas"
                  {...formik}
                />
              </div>
            </div>

            <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
              <button
                type="button"
                className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                onClick={onClose}
              >
                {t("Cancel")}
              </button>

              <button
                type="button"
                className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                onClick={formik.submitForm}
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? t("Saving...") : t("Save")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};
